import { FinanceRequestFormModal } from '@/components/Financeiro/solicitacoes/FinanceRequestFormModal'
import { FinanceRequestDetailPanel } from '@/components/Financeiro/FinanceRequestDetailPanel'
import { PortalPjHero } from '@/components/PortalPj/PortalPjHero'
import { PortalPjNfSection } from '@/components/PortalPj/PortalPjNfSection'
import { PortalPjMyRequests } from '@/components/PortalPj/PortalPjMyRequests'
import { PortalPjFeedbackSection } from '@/components/PortalPj/PortalPjFeedbackSection'
import { PortalPjRequestHub } from '@/components/PortalPj/PortalPjRequestHub'
import type { PortalPjTab } from '@/components/layout/PortalPjLayout'
import type { FinanceAreaKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import type { PortalTypeKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import type { FinanceTypeCard } from '@/hooks/useFinanceiroSolicitacoesPage'
import type { FinanceRequestDetail, FinanceRequestRow } from '@/services/financeService'
import type { PortalPjFeedback } from '@/services/portalPjFeedbackService'

interface PortalPjBodyProps {
  tab: PortalPjTab
  allowedAreas: FinanceAreaKey[]
  typesLoading: boolean
  typesByArea: Map<FinanceAreaKey, FinanceTypeCard[]>
  selectedType: PortalTypeKey | null
  setSelectedType: (type: PortalTypeKey | null) => void
  selectedCard: FinanceTypeCard | null
  submitting: boolean
  submitRequest: (
    typeKey: PortalTypeKey,
    formData: Record<string, string>,
    linkedEmpresaId?: string,
    linkedDealId?: string,
    attachments?: string[],
  ) => Promise<unknown>
  requests: FinanceRequestRow[]
  requestsLoading: boolean
  requestsError: string | null
  reloadRequests: () => void
  selectedRequestId: string | null
  requestDetail: FinanceRequestDetail | null
  detailLoading: boolean
  openRequest: (id: string) => void
  closeRequest: () => void
  addComment: (body: string, isInternal?: boolean, attachments?: string[]) => Promise<boolean>
  reloadRequestDetail: () => void
  canViewFinancialValues: boolean
  canViewClosedScope: boolean
  hideEmpresaSearch?: boolean
  embedded?: boolean
  typeLabels?: Record<string, string>
  feedbackItems?: PortalPjFeedback[]
  feedbackLoading?: boolean
  feedbackError?: string | null
}

export function PortalPjBody({
  tab,
  allowedAreas,
  typesLoading,
  typesByArea,
  selectedType,
  setSelectedType,
  selectedCard,
  submitting,
  submitRequest,
  requests,
  requestsLoading,
  requestsError,
  reloadRequests,
  selectedRequestId,
  requestDetail,
  detailLoading,
  openRequest,
  closeRequest,
  addComment,
  reloadRequestDetail,
  canViewFinancialValues,
  canViewClosedScope,
  hideEmpresaSearch = false,
  embedded = false,
  typeLabels,
  feedbackItems = [],
  feedbackLoading = false,
  feedbackError = null,
}: PortalPjBodyProps) {
  const panelClass = embedded
    ? 'space-y-4'
    : 'rounded-xl border border-white/10 bg-[#17072c]/95 p-5 shadow-2xl shadow-black/30 backdrop-blur sm:p-6'

  return (
    <>
      <div className={panelClass}>
        <PortalPjHero activeTab={tab} />

        {tab === 'nova' ? (
          typesLoading ? (
            <div className='flex justify-center py-20'>
              <div className='h-9 w-9 animate-spin rounded-full border-2 border-accent border-t-transparent' />
            </div>
          ) : (
            <PortalPjRequestHub
              typesByArea={typesByArea}
              allowedAreas={allowedAreas}
              onSelect={setSelectedType}
            />
          )
        ) : tab === 'nf' ? (
          <PortalPjNfSection />
        ) : tab === 'feedback' ? (
          <PortalPjFeedbackSection
            items={feedbackItems}
            loading={feedbackLoading}
            error={feedbackError}
          />
        ) : (
          <PortalPjMyRequests
            requests={requests}
            loading={requestsLoading}
            error={requestsError}
            selectedRequestId={selectedRequestId}
            typeLabels={typeLabels}
            onRefresh={() => void reloadRequests()}
            onSelect={openRequest}
          />
        )}
      </div>

      {selectedCard && (
        <FinanceRequestFormModal
          type={selectedCard}
          open={Boolean(selectedType)}
          submitting={submitting}
          presentation='portal'
          hideEmpresaSearch={hideEmpresaSearch}
          onClose={() => setSelectedType(null)}
          onSubmit={(formData, linkedEmpresaId, linkedDealId, attachments) => {
            if (!selectedType) return
            void submitRequest(selectedType, formData, linkedEmpresaId, linkedDealId, attachments)
          }}
        />
      )}

      <FinanceRequestDetailPanel
        presentation='portal'
        open={Boolean(selectedRequestId)}
        loading={detailLoading}
        detail={requestDetail}
        canManageQueue={false}
        canViewFinancialValues={canViewFinancialValues}
        canViewClosedScope={canViewClosedScope}
        assignees={[]}
        onClose={closeRequest}
        onUpdateStatus={async () => false}
        onAssign={async () => false}
        onAddComment={addComment}
        onReload={() => void reloadRequestDetail()}
      />
    </>
  )
}
